// Block M (UI-23): THE spotlight guide every guided tour runs on (mockups/guide-popup.html).
//
//   the page is dimmed and the step's target outlined (a pastel ring and an ink line round it)
//   a card beside the target, with an arrow pointing at it, a pastel step header, a segmented
//   progress bar, Back and Next, and on the last step "Don't show again" with Got it
//   arrow keys move, Escape closes, Tab stays in the card; the card takes focus, and focus goes
//   back to whatever opened it (the ? beside the page title, which restarts the tour)
//   the target is scrolled into view, and never covered: when the target is bigger than the room
//   left beside the card, the outline is drawn round the part of it that is showing
//   on a phone, and on every touch screen (a touchscreen laptop takes the phone path at any width),
//   the card is a bottom sheet and the outlined target sits in the space above it
//
// steps: [{ title, body, target, whenMissing }] (tours/guides.js says what each field means).
// onClose('dismissed') for Escape, Skip tour and the close button; onFinish({ dontShowAgain })
// for Got it. A step with no target, or whose target is not on screen, is centred, with nothing
// outlined; a 'skip' step whose target is not on screen is left out.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Layer, useFocusTrap } from './Overlay';
import { Button } from './Button';
import { Checkbox } from './Controls';
import { v } from './tokens';
import { MEDIA } from './breakpoints';
import './v2.css';
import './guide.css';

const PAD = 12;    // the card keeps this far from the screen's edges
const GAP = 16;    // between the outline and the card (the arrow sits in it)
const RING = 8;    // the outline stands this far off the target
const CARD_MAX = 400;

// The step headers take the pastels in turn (the mockup's blue, mint, yellow, orange, periwinkle).
export const GUIDE_COLOURS = ['pastel-blue', 'pastel-mint', 'pastel-yellow', 'pastel-orange', 'pastel-periwinkle', 'pastel-pink', 'pastel-lilac', 'pastel-green'];

// A phone, or any touch screen: the card is a bottom sheet.
function sheetMode() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(MEDIA.phone).matches || window.matchMedia('(pointer: coarse)').matches;
}

// On screen: it has a box, it is not hidden, and no clipping parent (a sideways-scrolling board,
// a closed drawer) hides it. Up and down does not count: the guide scrolls it into view.
export function onScreen(el) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return false;
  const r = el.getBoundingClientRect();
  if (!(r.width > 0 && r.height > 0)) return false;
  if (window.getComputedStyle(el).visibility === 'hidden') return false;
  let left = r.left;
  let right = r.right;
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const s = window.getComputedStyle(p);
    if (s.display === 'none') return false;
    if (s.overflowX && s.overflowX !== 'visible') {
      const pr = p.getBoundingClientRect();
      if (pr.width > 0) { left = Math.max(left, pr.left); right = Math.min(right, pr.right); }
    }
  }
  return right - left > 4 && right > 0 && left < window.innerWidth;
}

// The first copy of a target that is on screen (a page can mark several cards with one id).
export function findTarget(id, inside) {
  if (!id || typeof document === 'undefined') return null;
  const all = document.querySelectorAll(`[data-tour-id="${String(id).replace(/["\\]/g, '\\$&')}"]`);
  for (const el of all) if (!(inside && inside.contains(el)) && onScreen(el)) return el;
  return null;
}

// How far down the phone's sticky top bar reaches (0 where there is none).
function topInset() {
  const bar = document.querySelector('.ui-topbar');
  if (!bar) return 0;
  const r = bar.getBoundingClientRect();
  return r.height > 0 && r.bottom > 0 ? r.bottom : 0;
}

// Scrolls the target's scrolling parents (then the page) by dy.
function scrollTargetBy(el, dy) {
  let remaining = dy;
  for (let p = el.parentElement; p && Math.abs(remaining) > 1; p = p.parentElement) {
    if (p === document.body || p === document.documentElement) break;
    const s = window.getComputedStyle(p);
    if (/(auto|scroll|overlay)/.test(s.overflowY) && p.scrollHeight > p.clientHeight) {
      const before = p.scrollTop;
      p.scrollTop = before + remaining;
      remaining -= p.scrollTop - before;
    }
  }
  if (Math.abs(remaining) > 1) {
    const page = document.scrollingElement || document.documentElement;
    page.scrollTop += remaining;
  }
}

const grow = (r, by) => ({ top: r.top - by, left: r.left - by, width: r.width + by * 2, height: r.height + by * 2, bottom: r.bottom + by, right: r.right + by });
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

// Where the card goes beside an outlined box e, or null if it fits nowhere beside it.
function besideCard(e, { vw, vh, cw, ch, top }) {
  const B = vh - PAD;
  const cx = e.left + e.width / 2;
  const cy = e.top + e.height / 2;
  const across = { left: clamp(cx - cw / 2, PAD, vw - cw - PAD) };
  if (e.bottom + GAP + ch <= B) return { placement: 'below', top: e.bottom + GAP, ...across, arrow: { side: 'top', at: clamp(cx - across.left, 24, cw - 24) } };
  if (e.top - GAP - ch >= top) return { placement: 'above', top: e.top - GAP - ch, ...across, arrow: { side: 'bottom', at: clamp(cx - across.left, 24, cw - 24) } };
  const down = clamp(cy - ch / 2, top, B - ch);
  const at = clamp(cy - down, 56, ch - 24);
  if (e.right + GAP + cw <= vw - PAD) return { placement: 'right', top: down, left: e.right + GAP, arrow: { side: 'left', at } };
  if (e.left - GAP - cw >= PAD) return { placement: 'left', top: down, left: e.left - GAP - cw, arrow: { side: 'right', at } };
  return null;
}

// How far to scroll so the target (and, beside it, the card) is in view. 0 when it already is.
// sheet: false (a card), 'bottom' (the bottom sheet) or 'top' (the sheet moved under the top bar,
// for a target that stays at the foot of the screen: the floating Aidey button, a chat's message box).
export function planScroll(r, { vw, vh, cw, ch, sheet, inset }) {
  const e = grow(r, RING);
  const T = sheet === 'top' ? inset + ch + PAD : inset + PAD;
  const B = sheet && sheet !== 'top' ? vh - ch - PAD : vh - PAD;
  if (sheet) {
    if (e.top >= T && e.bottom <= B) return 0;
    return e.height <= B - T ? e.top - (T + (B - T - e.height) / 2) : e.top - T;
  }
  if (e.top >= T && e.bottom <= B && besideCard(e, { vw, vh, cw, ch, top: T })) return 0;
  if (e.height + GAP + ch <= B - T) return e.top - (T + (B - T - (e.height + GAP + ch)) / 2);
  if (e.height <= B - T) return e.top - (T + (B - T - e.height) / 2);
  return e.top - T;
}

// The outline and the card for a target box r, already in view.
export function placeGuide(r, { vw, vh, cw, ch, sheet, inset }) {
  const e = grow(r, RING);
  const T = inset + PAD;
  const clip = (box, top, bottom) => {
    const t = Math.max(box.top, top);
    const b = Math.min(box.top + box.height, bottom);
    const l = Math.max(box.left, 4);
    const rt = Math.min(box.left + box.width, vw - 4);
    return b - t > 8 && rt - l > 8 ? { top: t, left: l, width: rt - l, height: b - t } : null;
  };
  if (sheet === 'top') return { placement: 'sheet-top', spot: clip(e, inset + ch + PAD, vh - 4), card: { top: inset }, arrow: null };
  if (sheet) return { placement: 'sheet', spot: clip(e, inset + 4, vh - ch - PAD), card: null, arrow: null };
  const beside = besideCard(e, { vw, vh, cw, ch, top: T });
  if (beside) return { placement: beside.placement, spot: clip(e, inset + 4, vh - 4), card: { top: beside.top, left: beside.left }, arrow: beside.arrow };
  // Bigger than the room beside the card: the card sits at the foot of the screen and the outline
  // is drawn round the part of the target above it, so nothing outlined is ever covered.
  const top = vh - PAD - ch;
  const left = clamp(e.left + e.width / 2 - cw / 2, PAD, vw - cw - PAD);
  const spot = clip(e, inset + 4, top - GAP);
  return { placement: 'over', spot, card: { top, left }, arrow: spot ? { side: 'top', at: clamp(spot.left + spot.width / 2 - left, 24, cw - 24) } : null };
}

export function Guide({ steps = [], open, onClose, onFinish }) {
  const cardRef = useRef(null);
  const [active, setActive] = useState(null);   // the steps in this run (skip steps left out)
  const [i, setI] = useState(0);
  const [hide, setHide] = useState(false);
  const [geo, setGeo] = useState(null);         // { mode, placement, spot, card, arrow }
  const sideRef = useRef('bottom');             // where this step's sheet sits, decided once per step

  // Each opening starts again from the first step (the ? restarts the tour).
  useLayoutEffect(() => {
    if (!open) { setActive(null); setGeo(null); return; }
    setActive(steps.filter((s) => !(s.whenMissing === 'skip' && s.target && !findTarget(s.target))));
    setI(0);
    setHide(false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = active && active[i];
  const last = !!active && i === active.length - 1;
  const dismiss = useCallback(() => onClose?.('dismissed'), [onClose]);
  useFocusTrap(cardRef, !!open && !!active, { onEscape: dismiss, initialFocus: cardRef });

  // Lay out: find the target, scroll it into view (once per step), then place the outline and card.
  const place = useCallback((scroll) => {
    const card = cardRef.current;
    if (!step || !card) return;
    const sheet = sheetMode();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cw = sheet ? vw : Math.min(CARD_MAX, vw - PAD * 2);
    const ch = card.offsetHeight || 0;
    const inset = topInset();
    const el = findTarget(step.target, card);
    if (!el) { setGeo({ mode: sheet ? 'sheet' : 'card', placement: 'centre', spot: null, card: null, arrow: null }); return; }
    const opts = { vw, vh, cw, ch, sheet: sheet ? 'bottom' : false, inset };
    if (scroll) {
      sideRef.current = 'bottom';
      const dy = planScroll(el.getBoundingClientRect(), opts);
      if (Math.abs(dy) > 1) scrollTargetBy(el, dy);
      // A target that will not come up above the bottom sheet (it is fixed to the foot of the
      // screen, or the page cannot scroll any further) is shown with the sheet at the top instead,
      // so the sheet never covers it.
      if (sheet) {
        const r = el.getBoundingClientRect();
        const first = placeGuide(r, opts);
        if (!first.spot || first.spot.height < Math.min(r.height + RING * 2, 32)) {
          const top = { ...opts, sheet: 'top' };
          const dy2 = planScroll(r, top);
          if (Math.abs(dy2) > 1) scrollTargetBy(el, dy2);
          if (placeGuide(el.getBoundingClientRect(), top).spot) sideRef.current = 'top';
        }
      }
    }
    if (sheet) opts.sheet = sideRef.current;
    const g = placeGuide(el.getBoundingClientRect(), opts);
    setGeo({ mode: sheet ? 'sheet' : 'card', ...g, target: step.target });
  }, [step]);

  useLayoutEffect(() => {
    if (!step) return undefined;
    place(true);
    // Fonts, images and late data change sizes after the first paint: place again shortly after.
    const t1 = setTimeout(() => place(false), 120);
    const t2 = setTimeout(() => place(false), 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [step, place]);

  useEffect(() => {
    if (!step) return undefined;
    const again = () => place(false);
    window.addEventListener('resize', again);
    window.addEventListener('scroll', again, true);
    return () => { window.removeEventListener('resize', again); window.removeEventListener('scroll', again, true); };
  }, [step, place]);

  // A new step: the card keeps focus (a screen reader reads the new title).
  useEffect(() => { if (step && cardRef.current && !cardRef.current.contains(document.activeElement)) cardRef.current.focus({ preventScroll: true }); }, [step]);

  const go = useCallback((n) => { if (active && n >= 0 && n < active.length) setI(n); }, [active]);
  useEffect(() => {
    if (!step) return undefined;
    const onKey = (e) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.target && e.target.matches && e.target.matches('input:not([type="checkbox"]), textarea, select')) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); go(i + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(i - 1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [step, i, go]);

  if (!open || !step) return null;

  const colour = GUIDE_COLOURS[i % GUIDE_COLOURS.length];
  const mode = geo?.mode || (sheetMode() ? 'sheet' : 'card');
  const placement = geo?.placement || (step.target ? 'pending' : 'centre');
  const cardStyle = { '--ui-bg': v(colour) };
  if (mode === 'card' && geo?.card) { cardStyle.top = geo.card.top; cardStyle.left = geo.card.left; }
  if (mode === 'sheet' && placement === 'sheet-top' && geo?.card) cardStyle.top = geo.card.top;
  const titleId = 'ui-guide-title';

  return (
    <Layer>
      <div className="ui-guide" data-guide="">
        {/* Clicks on the page do nothing while the guide is open (the mockup's dimmed page). */}
        <div className="ui-guide__catch" aria-hidden="true" />
        {geo?.spot ? (
          <div className="ui-guide__spot" data-guide-spotlight={geo.target} aria-hidden="true"
            style={{ top: geo.spot.top, left: geo.spot.left, width: geo.spot.width, height: geo.spot.height, '--ui-bg': v(colour) }} />
        ) : (
          <div className="ui-guide__dim" aria-hidden="true" />
        )}
        <div ref={cardRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby="ui-guide-text" tabIndex={-1}
          className={`ui-guide__card ui-guide__card--${mode}`} data-guide-mode={mode} data-guide-placement={placement}
          data-guide-step={i + 1} style={cardStyle}>
          {mode === 'card' && geo?.arrow && (
            <span className={`ui-guide__arrow ui-guide__arrow--${geo.arrow.side}`} data-guide-arrow={geo.arrow.side} aria-hidden="true"
              style={geo.arrow.side === 'top' || geo.arrow.side === 'bottom' ? { left: geo.arrow.at - 8 } : { top: geo.arrow.at - 8 }} />
          )}
          <div className="ui-guide__band">
            <span className="ui-guide__step">Step {i + 1} of {active.length}</span>
            <span className="ui-guide__band-end">
              {!last && <button type="button" className="ui-guide__skip ui-hit" onClick={dismiss}>Skip tour</button>}
              <button type="button" className="ui-guide__close ui-overlay__close ui-hit" aria-label="Close guide" onClick={dismiss}><span aria-hidden="true">✕</span></button>
            </span>
          </div>
          <div className="ui-guide__body">
            <h2 className="ui-guide__title" id={titleId}>{step.title}</h2>
            <p className="ui-guide__text" id="ui-guide-text">{step.body}</p>
            <div className="ui-guide__progress" role="group" aria-label={`Step ${i + 1} of ${active.length}`}>
              {active.map((s, k) => (
                <button key={k} type="button" className={`ui-guide__seg${k <= i ? ' ui-guide__seg--done' : ''}`}
                  aria-label={`Go to step ${k + 1}`} aria-current={k === i ? 'step' : undefined} onClick={() => go(k)}>
                  <span aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>
          <div className="ui-guide__foot">
            {last
              ? <Checkbox checked={hide} onChange={setHide} label="Don’t show again" />
              : mode === 'card' && <span className="ui-guide__hint">Use ← → keys</span>}
            <span className="ui-guide__buttons">
              {i > 0 && <Button variant="secondary" size="sm" onClick={() => go(i - 1)}>Back</Button>}
              {last
                ? <Button variant="confirm" size="sm" onClick={() => onFinish?.({ dontShowAgain: hide })}>Got it</Button>
                : <Button variant="confirm" size="sm" onClick={() => go(i + 1)}>Next →</Button>}
            </span>
          </div>
        </div>
      </div>
    </Layer>
  );
}
